import { formatPriceWithCurrencySymbol } from "../../../helpers/getPriceWithCurrencySymbol";
import BookingGetters from "../../../store/onlineBooking/getters";
import { InsuranceCardProps } from "./Insurance.props";
import React, { useEffect, useState } from "react";
import AppButton from "../../UI/AppButtons";
import { t } from "i18next";
import { differenceInDays, format } from "date-fns";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

function InsuranceCardsLayout({
  insurances,
  onSelected,
  selectedInsuranceIndex = -1,
  secondaryColor = "",
  vehicleGroup,
  insuranceBody,
  totalHeightRecepInsuranceFooter,
}: InsuranceCardProps) {
  const [isAndroid, setIsAndroid] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent)) {
      setIsAndroid(true);
    }

    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function getParentOrigin() {
    try {
      if (
        window.location.ancestorOrigins &&
        window.location.ancestorOrigins.length > 0
      ) {
        return window.location.ancestorOrigins[0];
      }
      if (document.referrer) {
        return new URL(document.referrer).origin;
      }
    } catch (error) {
      console.error("Error in getParentOrigin:", error);
      return "";
    }
  }
  const isDesktopScreen = window.innerWidth >= 1310;
  const parentOrigin = getParentOrigin();
  const allowedOrigins = new Set([
    "https://mbrent.mercedes-benz.it",
    "https://bookingmerfina.mbrent.mercedes-benz.it",
    "https://mbrent.mercedes-benz.it/congaratulation",
    "https://merfinatestbooking.myrent.it",
    "https://mauroc147.myrent.it",
  ]);

  const targetOrigin =
    parentOrigin && allowedOrigins.has(parentOrigin) ? parentOrigin : "*";

  function requestParentResize() {
    window.parent.postMessage(
      {
        event_id: "iframeHeight",
        data: {
          iframeHeight: isSmallScreen
            ? isAndroid
              ? 1025
              : 1000
            : totalHeightRecepInsuranceFooter,
        },
      },
      targetOrigin
    );
  }

  useEffect(() => {
    requestParentResize();
  }, [insurances, isSmallScreen]);

  const allInsuranceFeatures: Record<string, boolean> = {};
  const allInsurances = insurances.map((el) => {
    const availableInsurances = el.features.reduce((prev, current) => {
      const feature = current.toLowerCase();
      prev[feature] = true;
      allInsuranceFeatures[feature] = true;
      return prev;
    }, {});
    return {
      bookingText: undefined,
      ...el,
      availableInsurances,
    };
  });

  const currencyDetails = BookingGetters.getCurrency();
  const boxShadow = "shadow-lg";
  const [expandedIndex] = useState(-1);

  useEffect(() => {
    const freeInsuranceIndex = allInsurances.findIndex(
      (el) => parseFloat(el.pricePerDayInUSD) === 0
    );
    if (freeInsuranceIndex !== -1 && selectedInsuranceIndex === -1) {
      onSelected(freeInsuranceIndex);
    }
  }, [allInsurances]);

  const handleCardClick = (index: number) => {
    const clickedInsurance = allInsurances[index];
    if (parseFloat(clickedInsurance.pricePerDayInUSD) === 0) {
      return;
    }
    if (selectedInsuranceIndex === index) {
      onSelected(-1);
    } else {
      onSelected(index);
    }
  };

  const ReservationDetails = BookingGetters.getBookingDetails();
  const [startDate] = useState(
    new Date(
      format(
        new Date(ReservationDetails.pickUpDateAndTime || Date.now()),
        "yyyy-MM-dd"
      )
    )
  );
  const [endDate] = useState(
    new Date(
      format(
        new Date(ReservationDetails.dropOffDateAndTime || Date.now()),
        "yyyy-MM-dd"
      )
    )
  );
  const { t: translate } = useTranslation();
  const [numberOfDays] = useState(differenceInDays(endDate, startDate));
  // Helper function to get insurance body content
  const getInsuranceBodyContent = (insuranceBody, el, vehicleGroup) => {
    const key = `${el?.name}:${vehicleGroup}`;
    const content = insuranceBody[key];
    return content ? (
      <div
        style={{
          whiteSpace: "pre-line",
          fontSize: "small",
          fontFamily: "MBCorpoSText-Regular-Web-new",
        }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content),
        }}
      />
    ) : null;
  };

  // Helper function to get insurance feature content
  const getInsuranceFeatureContent = (feature, el) => {
    const key = `${feature.toUpperCase()}:${el.name.toUpperCase()}`;
    const content = el.bookingText?.[key];
    return content ? (
      <div
        style={{
          whiteSpace: "pre-line",
          fontSize: "small",
          fontFamily: "MBCorpoSText-Regular-Web-new",
        }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(content),
        }}
      />
    ) : (
      <p
        className="text-md"
        style={{
          textAlign: "left",
          fontFamily: "MBCorpoSText-Regular-Web-new",
          fontSize: "16px",
        }}
      >
        {feature}
      </p>
    );
  };

  return (
    <div
      className="flex flex-col"
      style={{
        height: "100%",
        minHeight: "100px",
        // marginBottom: isSmallScreen ? (isAndroid ? "0" : "0") : "0",
        // marginTop: isSmallScreen ? (isAndroid ? "0" : "0") : "0",
      }}
    >
      <h2
        className={`text-black ${
          isSmallScreen ? (isAndroid ? "mb-2" : "mb-4") : "mb-4"
        }`}
        style={{
          gridColumn: "1 / -1",
          fontFamily: "MBCorpoAScreen1Cond-Regular-Web",
          fontSize: isSmallScreen ? "24px" : "32px",
          paddingLeft: isSmallScreen ? "20px" : "0",
        }}
      >
        {t("Add Insurance")}
      </h2>
      <hr
        style={{
          marginTop: !isSmallScreen ? "17px" : "0",
          border: "none",
          borderTop: "1px solid #000000",
          marginRight: isDesktopScreen ? "2%" : "undefined",
        }}
      />
      <div
        className={`flex-1 overflow-y-auto ${isSmallScreen ? "px-4" : "px-4"}`}
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div
          className={`grid grid-cols-1 ${
            !isSmallScreen ? "md:grid-cols-2 lg:grid-cols-3" : ""
          } gap-3 box-border`}
          style={{
            paddingBottom: isSmallScreen ? "20px" : "0",
          }}
        >
          {allInsurances.map((el, index) => (
            <div
              className={`${boxShadow} ${
                isSmallScreen ? "mb-4" : "m-8"
              } p-6 flex-1 flex flex-col bg-white`}
              style={{
                width: isSmallScreen ? "100%" : "88%",
                cursor:
                  parseFloat(el.pricePerDayInUSD) != 0
                    ? "pointer"
                    : "not-allowed",
              }}
              key={index}
            >
              <h5
                className="text-xl font-semibold mb-2"
                style={{
                  color: secondaryColor,
                  fontFamily: "MBCorpoAScreen1Cond-Regular-Web",
                }}
              >
                {el.name}
              </h5>
              <div className="text-center text-black flex-grow">
                {/* {insuranceBody &&
                insuranceBody[el?.name + ":" + vehicleGroup] ? (
                  <div
                    style={{
                      whiteSpace: "pre-line",
                      fontSize: "small",
                      fontFamily: "MBCorpoSText-Regular-Web-new",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        insuranceBody[el?.name + ":" + vehicleGroup] || ""
                      ),
                    }}
                  />
                ) : (
                  Object.keys(allInsuranceFeatures).map((feature, i) => (
                    <div key={i} className="flex items-left mb-2">
                      {el.bookingText &&
                      el.bookingText[
                        feature.toUpperCase() + ":" + el.name.toUpperCase()
                      ] ? (
                        <div
                          style={{
                            whiteSpace: "pre-line",
                            fontSize: "small",
                            fontFamily: "MBCorpoSText-Regular-Web-new",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                              el.bookingText[
                                feature.toUpperCase() +
                                  ":" +
                                  el.name.toUpperCase()
                              ]
                            ),
                          }}
                        />
                      ) : (
                        <>
                          <p
                            className="text-md"
                            style={{
                              textAlign: "left",
                              fontFamily: "MBCorpoSText-Regular-Web-new",
                              fontSize: "16px",
                            }}
                          >
                            {feature}
                          </p>
                        </>
                      )}
                    </div>
                  ))
                )} */}
                {insuranceBody &&
                getInsuranceBodyContent(insuranceBody, el, vehicleGroup)
                  ? getInsuranceBodyContent(insuranceBody, el, vehicleGroup)
                  : Object.keys(allInsuranceFeatures).map((feature, i) => (
                      <div key={i} className="flex items-left mb-2">
                        {getInsuranceFeatureContent(feature, el)}
                      </div>
                    ))}
              </div>
              {el.pricePerDayInUSD > 0 && (
                <p
                  className="text-lg mb-2 font-bold mt-auto"
                  style={{
                    color: "black",
                    fontFamily: "MBCorpoAScreen1Cond-Regular-Web",
                  }}
                >
                  <span style={{ fontSize: "25px" }}>
                    {numberOfDays == 0
                      ? el.pricePerDayInUSD
                      : (
                          parseFloat(el.pricePerDayInUSD) / numberOfDays
                        )?.toFixed(2)}
                  </span>{" "}
                  {formatPriceWithCurrencySymbol(currencyDetails).symbol + " "}
                  {translate("/day")}
                </p>
              )}
              <AppButton
                className="h-14 w-full"
                color={
                  selectedInsuranceIndex == index || el.pricePerDayInUSD == 0
                    ? "primary"
                    : "secondaryColor"
                }
                onClick={() => {
                  parseFloat(el.pricePerDayInUSD) != 0
                    ? handleCardClick(index)
                    : "";
                }}
                style={{
                  borderRadius: "5px",
                  marginTop: "9px",
                  cursor:
                    parseFloat(el.pricePerDayInUSD) != 0
                      ? "pointer"
                      : "not-allowed",
                }}
              >
                <p className="w-full text-center">
                  {el.pricePerDayInUSD == 0 ? (
                    <span>{t("Include")}</span>
                  ) : (
                    <span>
                      {expandedIndex === index ? t("Include") : t("Select")}
                    </span>
                  )}
                </p>
              </AppButton>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export { InsuranceCardsLayout };
